import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";

/**
 * Boleto/Pix Banco Inter (BolePix) — ficha imprimível.
 * Linha digitável, código de barras, nosso número e QR Pix vêm SEMPRE da
 * cobrança oficial emitida no Banco Inter (`/pagamentos/.../pix/preparar`).
 * Nada é gerado localmente: se o Inter não devolveu, o campo fica indisponível.
 */

const fmtBRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const L = { fontSize: 7, color: "#555", textTransform: "uppercase" };
const fmtLinha = (l) => {
  const d = String(l || "").replace(/\D/g, "");
  if (d.length !== 47) return l || "";
  return `${d.slice(0, 5)}.${d.slice(5, 10)} ${d.slice(10, 15)}.${d.slice(15, 21)} ${d.slice(21, 26)}.${d.slice(26, 32)} ${d.slice(32, 33)} ${d.slice(33)}`;
};

function unidadesLabel(contrato) {
  const us = contrato?.unidades || [];
  return us.map((u) => u.numero_completo || u.numero || u.apelido).filter(Boolean).join(" · ") || "—";
}

function Campo({ label, children, style }) {
  return (
    <div style={{ padding: 4, ...style }}>
      <div style={L}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 10 }}>{children}</div>
    </div>
  );
}

function CodigoBarras({ codigo }) {
  const ref = useRef(null);
  const valido = /^\d{44}$/.test(codigo || "");
  useEffect(() => {
    if (ref.current && valido) {
      try { JsBarcode(ref.current, codigo, { format: "ITF", width: 1.4, height: 42, displayValue: false, margin: 0 }); } catch { /* noop */ }
    }
  }, [codigo, valido]);
  if (!valido) return <div style={{ fontSize: 9, color: "#b45309" }} data-testid="boleto-barras-indisponivel">Código de barras indisponível — pague pelo Pix ao lado.</div>;
  return <svg ref={ref} style={{ width: "100%", maxHeight: 46 }} data-testid="boleto-codigo-barras" />;
}

function PixQr({ copiaCola, base64 }) {
  if (base64) return <img src={`data:image/png;base64,${base64}`} alt="QR Code Pix" style={{ width: 92, height: 92, objectFit: "contain" }} />;
  if (copiaCola) return <QRCodeSVG value={copiaCola} size={92} includeMargin={false} />;
  return (
    <div style={{ width: 92, height: 92, border: "1px dashed #b45309", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", color: "#b45309", fontSize: 8, padding: 4, textAlign: "center" }}>
      Pix indisponível — gere no portal
    </div>
  );
}

export default function BoletoFebraban({ boleto, contrato, beneficiario }) {
  const benef = beneficiario || {};
  const linha = fmtLinha(boleto?.linha_digitavel);
  const vencimento = boleto?.vencimento_cobranca || boleto?.vencimento;
  const valor = boleto?.valor_cobrado ?? boleto?.valor_total;
  const venceuOriginal = boleto?.vencimento_cobranca && boleto?.vencimento && boleto.vencimento_cobranca !== String(boleto.vencimento).slice(0, 10);

  return (
    <div className="print-container print-boleto" data-testid="boleto-febraban" style={{ fontFamily: "Arial, sans-serif", background: "white", color: "black", padding: 8 }}>
      <div style={{ borderBottom: "2px solid #000", display: "flex", alignItems: "center", padding: "4px 0" }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#FF7A00", paddingRight: 8, borderRight: "2px solid #000" }} data-testid="boleto-banco-nome">{benef.banco_nome || "Banco Inter"}</div>
        <div style={{ fontSize: 14, fontWeight: 900, padding: "0 8px", borderRight: "2px solid #000" }} data-testid="boleto-banco-codigo">{benef.banco_codigo || "077"}-9</div>
        <div style={{ flex: 1, textAlign: "right", fontSize: 10, fontWeight: 700, fontFamily: "Consolas, monospace" }} data-testid="boleto-linha-digitavel">
          {linha || "Linha digitável indisponível — use o Pix"}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #000", borderTop: "none" }}>
        <div style={{ padding: 4, borderRight: "1px solid #000" }}>
          <div style={L}>Beneficiário</div>
          <div style={{ fontWeight: 700, fontSize: 10 }}>{benef.razao_social}</div>
          <div style={{ fontSize: 9 }}>CNPJ: {benef.cnpj}</div>
          <div style={{ fontSize: 8 }}>{benef.endereco}</div>
        </div>
        <div>
          <Campo label="Agência / Cód. Beneficiário">{benef.agencia_codigo}</Campo>
          <Campo label="Nosso Número"><span style={{ fontFamily: "monospace" }}>{boleto?.nosso_numero || "—"}</span></Campo>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", border: "1px solid #000", borderTop: "none", fontSize: 9 }}>
        {[["Competência", boleto?.competencia || "—"], ["Nº documento", String(boleto?.id || "").slice(-8).toUpperCase()], ["Espécie", "R$"], ["Carteira", benef.carteira || "112"], ["Processamento", fmtDate(new Date().toISOString())]].map(([lbl, val], i) => (
          <Campo key={lbl} label={lbl} style={{ borderRight: i < 4 ? "1px solid #000" : "none" }}>{val}</Campo>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", border: "1px solid #000", borderTop: "none" }}>
        <div style={{ padding: 4, borderRight: "1px solid #000", fontSize: 9, lineHeight: 1.35 }}>
          <div style={L}>Instruções</div>
          {venceuOriginal && <div><b>Vencimento original {fmtDate(boleto.vencimento)}</b> — valor já atualizado com multa e juros até {fmtDate(vencimento)}. Pagável somente nesta data.</div>}
          {!venceuOriginal && <div>Após o vencimento: multa de <b>{boleto?.multa_atraso_percent ?? 2}%</b> e juros de <b>{boleto?.mora_diaria_percent ?? 0.033}% ao dia</b> (novo Pix é emitido no portal).</div>}
          <div style={{ marginTop: 2 }}>
            <b>Discriminação:</b> Aluguel {fmtBRL(boleto?.valor_aluguel)} · IPTU {fmtBRL(boleto?.valor_iptu)} · Água {fmtBRL(boleto?.valor_agua)} · Rateio {fmtBRL(boleto?.valor_rateio)}
            {(boleto?.multa || 0) > 0 && <> · Multa {fmtBRL(boleto.multa)}</>}
            {(boleto?.juros || 0) > 0 && <> · Juros {fmtBRL(boleto.juros)}</>}
          </div>
        </div>
        <div>
          <Campo label="Vencimento" style={{ borderBottom: "1px solid #000" }}><span style={{ fontSize: 14, fontWeight: 900 }} data-testid="boleto-vencimento">{fmtDate(vencimento)}</span></Campo>
          <Campo label="Valor do documento"><span style={{ fontSize: 17, fontWeight: 900 }} data-testid="boleto-valor">{fmtBRL(valor)}</span></Campo>
        </div>
      </div>

      <div style={{ border: "1px solid #000", borderTop: "none", padding: 4, fontSize: 9 }}>
        <div style={L}>Pagador</div>
        <div style={{ fontWeight: 700, fontSize: 10 }}>{contrato?.razao_social || "—"}</div>
        <div>CPF/CNPJ: {contrato?.cnpj || contrato?.cpf_cnpj || "—"} · Responsável: {contrato?.responsavel || "—"}</div>
        <div>Unidade(s): {unidadesLabel(contrato)}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", border: "1px solid #000", borderTop: "none", alignItems: "center", padding: 4, gap: 4 }}>
        <div>
          <div style={{ ...L, marginBottom: 2 }}>Código de Barras</div>
          <CodigoBarras codigo={boleto?.codigo_barras} />
        </div>
        <div style={{ textAlign: "center", borderLeft: "1px solid #000", paddingLeft: 4 }} data-testid="boleto-pix">
          <div style={{ ...L, marginBottom: 2 }}>Pix Banco Inter</div>
          <PixQr copiaCola={boleto?.pix_copia_cola} base64={boleto?.pix_qr_code_base64} />
        </div>
      </div>
    </div>
  );
}
